import { Input } from '@ohear/ui'
/**
 * Phone-number input with E.164 formatting helpers.
 *
 * Inputs vary wildly in format ('(415) 555-0100', '4155550100', '+1 415
 * 555 0100'), so on blur we normalize to E.164: a leading `+`, the
 * country code, and digits only. The default country (US/+1) is used
 * when the user typed digits without a `+`.
 *
 * Validation is deliberately permissive — the API does the authoritative
 * regex check. We just want the value sent to the server to look
 * sane.
 */
import { type ChangeEvent, type FocusEvent, useCallback } from 'react'

/** Normalize an arbitrary phone string to E.164.
 *
 *  Rules:
 *   - If it starts with `+`, keep the `+` and strip everything but digits.
 *   - Otherwise strip non-digits, prepend the default country code.
 *   - Returns the original string unchanged if it's empty (user cleared).
 */
export function toE164(raw: string, defaultCountryDial = '1'): string {
  const trimmed = raw.trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('+')) {
    const digits = trimmed.slice(1).replace(/\D+/g, '')
    return digits ? `+${digits}` : ''
  }
  const digits = trimmed.replace(/\D+/g, '')
  if (!digits) return ''
  // Strip a leading "0" that some locales prefix on national-format numbers.
  const stripped = digits.replace(/^0+/, '')
  return `+${defaultCountryDial}${stripped}`
}

/** Light validity check — used to show inline help text, not to block save. */
export function looksLikeE164(value: string): boolean {
  if (!value) return true // empty is allowed
  return /^\+[0-9]{8,15}$/u.test(value)
}

export interface PhoneInputProps {
  /** Current E.164 value (or empty string). */
  value: string | null | undefined
  /** Called with the normalized E.164 string on blur. */
  onChange: (next: string) => void
  /** Optional country dial code default (defaults to '1' / US). */
  defaultCountryDial?: string
  /** Display "Verified" pill on the right when true. */
  verified?: boolean
  placeholder?: string
  disabled?: boolean
  id?: string
  name?: string
  /** Called when the user clicks the trailing Verify action. */
  onRequestVerify?: () => void
  /** Optional label rendered above the input ("Verify" link disabled until value). */
  verifyLabel?: string
}

/**
 * Phone input that normalizes to E.164 on blur and exposes a "Verify"
 * trigger on the right when `onRequestVerify` is provided.
 *
 * The component is deliberately uncontrolled at the formatting level
 * (raw text is kept locally; the normalized value is reported via
 * `onChange` only after blur). This avoids fighting the user mid-type.
 */
export function PhoneInput(props: PhoneInputProps) {
  const {
    value,
    onChange,
    defaultCountryDial = '1',
    verified,
    placeholder = '+1 415 555 0100',
    disabled,
    id,
    name,
    onRequestVerify,
    verifyLabel = 'Verify',
  } = props

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      // Forward raw text up; parent re-renders with the new string. We only
      // normalize on blur so the cursor doesn't jump while typing.
      onChange(e.target.value)
    },
    [onChange],
  )

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const normalized = toE164(e.target.value, defaultCountryDial)
      if (normalized !== e.target.value) onChange(normalized)
    },
    [onChange, defaultCountryDial],
  )

  return (
    <div className="relative flex items-stretch gap-2">
      <Input
        id={id}
        name={name}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        value={value ?? ''}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className="flex-1"
      />
      {verified ? (
        <span
          className="self-center text-[12px] font-medium text-[var(--success-text,#0a8f4d)]"
          aria-label="Verified phone number"
        >
          Verified
        </span>
      ) : onRequestVerify ? (
        <button
          type="button"
          onClick={onRequestVerify}
          disabled={disabled || !value}
          className="self-center text-[13px] font-medium text-[var(--accent,#3b82f6)] disabled:text-[var(--text-3)] disabled:cursor-not-allowed"
        >
          {verifyLabel}
        </button>
      ) : null}
    </div>
  )
}
